import { FormControl, FormLabel, Stack } from '@mui/joy';
import { upperFirst } from 'lodash';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { IUom } from 'parts/types';
import { PricingSourceType } from 'pricing/enums';
import { useCostDecimals } from 'pricing/hooks';
import { CostLockTooltip, PriceLockTooltip } from 'quotes/elements';
import { PricingStrategy } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuotePart } from 'quotes/types';
import { getPurchaseCostLabel } from 'quotes/utils';
import { Size, TooltipPosition } from 'theme/enums';

import { QuotePartUpdateBulkField } from './enums';
import { QuotePartUpdateBulkFormData } from './types';
import { getFormField } from './utils';

interface IProps {
  isOpen: boolean;
  onSubmit: (
    reset?: UseFormReset<QuotePartUpdateBulkFormData>
  ) => (data: QuotePartUpdateBulkFormData) => void;
  onClose: (reset?: UseFormReset<QuotePartUpdateBulkFormData>, closeSelection?: boolean) => void;
  uoms: IUom[];
  isAlternateVersionEnabled: boolean;
  editableFields?: QuotePartUpdateBulkField[];
  selectedQuoteParts: ICustomerQuotePart[];
}

export const QuotePartUpdateBulkFormView = ({
  isOpen,
  onSubmit,
  onClose,
  uoms,
  isAlternateVersionEnabled,
  editableFields = Object.values(QuotePartUpdateBulkField),
  selectedQuoteParts,
}: IProps) => {
  const { t } = useTranslation();
  const costDecimals = useCostDecimals();

  const { control, errors, setValue, watch } = useFormContext();

  const atLeastOneQuotePartNonCustomCosting = selectedQuoteParts.some(
    (quotePart) => PricingSourceType.CUSTOM !== quotePart.costSourceType
  );

  const hideEditVendorQuoteNumber = !(
    editableFields.includes(QuotePartUpdateBulkField.VENDOR_QUOTE_NUMBER) &&
    (!atLeastOneQuotePartNonCustomCosting ||
      (atLeastOneQuotePartNonCustomCosting && !!watch('editPurchaseCost')))
  );

  const quotePartLinesWithPriceLocked = selectedQuoteParts
    .filter((quotePart) => QuotePartHelper.isPriceLocked(quotePart))
    .flatMap((quotePart) => [quotePart.line, quotePart.secondaryLine].filter(Boolean).join('.'));

  const quotePartLinesWithCostLocked = selectedQuoteParts
    .filter((quotePart) => QuotePartHelper.isCostLocked(quotePart))
    .flatMap((quotePart) => [quotePart.line, quotePart.secondaryLine].filter(Boolean).join('.'));

  const priceLockedTooltipTitle = quotePartLinesWithPriceLocked.length
    ? t('messageEditLinesWithPriceLocked', {
        linesWithPriceLocked: quotePartLinesWithPriceLocked.join(', '),
      })
    : null;

  const costLockedTooltipTitle = quotePartLinesWithCostLocked.length
    ? t('messageEditLinesWithCostLocked', {
        linesWithCostLocked: quotePartLinesWithCostLocked.join(', '),
      })
    : null;

  const defaultBulkPriceBasedOn = useMemo(
    () => QuotePartHelper.getConsensusPriceBasedOn(selectedQuoteParts),
    [selectedQuoteParts]
  );

  const onEditFieldChange = (field: QuotePartUpdateBulkField, checked: boolean) => {
    if ([QuotePartUpdateBulkField.PRICE, QuotePartUpdateBulkField.MARGIN].includes(field)) {
      setValue(QuotePartUpdateBulkField.PRICE === field ? 'editMargin' : 'editPrice', false);
    }

    if (
      QuotePartUpdateBulkField.PURCHASE_COST === field &&
      editableFields.includes(QuotePartUpdateBulkField.VENDOR_QUOTE_NUMBER)
    ) {
      setValue('editVendorQuoteNumber', checked);
    }

    if (QuotePartUpdateBulkField.PRICE_BASED_ON === field && checked && !watch('priceBasedOn')) {
      setValue('priceBasedOn', defaultBulkPriceBasedOn);
    }

    if (
      QuotePartUpdateBulkField.PRICING_STRATEGY === field &&
      checked &&
      !watch('pricingStrategy')
    ) {
      setValue('pricingStrategy', PricingStrategy.EXTERNAL);
    }
  };

  return (
    <Form title={t('titleUpdateSelected')} isOpen={isOpen} onClose={onClose} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <FormControl size={Size.LG}>
          <FormLabel required>{t('labelSelectFieldsToUpdate')}</FormLabel>
        </FormControl>

        {editableFields.map((field) => {
          const editFieldName = `edit${upperFirst(field)}`;

          let disabledField = false;
          let endDecorator = <></>;

          if (field === QuotePartUpdateBulkField.VENDOR_QUOTE_NUMBER && hideEditVendorQuoteNumber) {
            return;
          }

          const isPriceMarginLockedField =
            [QuotePartUpdateBulkField.PRICE, QuotePartUpdateBulkField.MARGIN].includes(field) &&
            quotePartLinesWithPriceLocked.length > 0;

          const isPriceBasedOnAllLocked =
            field === QuotePartUpdateBulkField.PRICE_BASED_ON &&
            selectedQuoteParts.length > 0 &&
            quotePartLinesWithPriceLocked.length === selectedQuoteParts.length;

          if (isPriceMarginLockedField || isPriceBasedOnAllLocked) {
            disabledField = true;
            endDecorator = (
              <PriceLockTooltip
                quotePart={selectedQuoteParts[0]}
                placement={TooltipPosition.BOTTOM_START}
                tooltipTitle={priceLockedTooltipTitle}
              />
            );
          }

          if (
            [
              QuotePartUpdateBulkField.PURCHASE_COST,
              QuotePartUpdateBulkField.PRIORITIZE_LEGACY_COST,
            ].includes(field) &&
            quotePartLinesWithCostLocked.length > 0
          ) {
            disabledField = true;
            endDecorator = (
              <CostLockTooltip
                quotePart={selectedQuoteParts[0]}
                placement={TooltipPosition.BOTTOM_START}
                tooltipTitle={costLockedTooltipTitle}
              />
            );
          }

          let fieldLabel = t(`label${upperFirst(field)}`);

          if (field === QuotePartUpdateBulkField.PURCHASE_COST) {
            fieldLabel = getPurchaseCostLabel(isAlternateVersionEnabled);
          }

          return (
            <Stack key={field} spacing={1}>
              <FormField
                key={editFieldName}
                name={editFieldName}
                type={InputType.CHECKBOX}
                inputProps={{
                  control,
                  error: errors?.[editFieldName] as unknown,
                  label: fieldLabel,
                  onChange: (event) => onEditFieldChange(field, event.target.checked),
                  size: Size.MD,
                  disabled: disabledField,
                  endDecorator,
                }}
              />

              {getFormField(
                field,
                control,
                errors,
                !!watch(editFieldName),
                watch,
                uoms,
                isAlternateVersionEnabled,
                selectedQuoteParts,
                costDecimals
              )}
            </Stack>
          );
        })}
      </Stack>
    </Form>
  );
};
